import { generateRandomString } from "utils/utils";
import { createLogger } from "utils/logging";
import { fetchRetry } from "utils/fetch";
import { preventClickBubbling } from "utils/events";
import { type DataContainer, type Feature, type FeatureStatus, submitMerge } from "@otto-ec/tracking-bct";
import { captureError } from "@otto-ec/global-resources/apm";
import { toggle } from "@otto-ec/global-resources/toggle";

const attributeNameId = "id";
const attributeNameBenefitId = "benefit-id";
const attributeNameOriginFeature = "feature";
const attributeNameTeam = "team";
const attributeNameVariationId = "variation-id";
const attributeNameTargetContextDreson = "target-context-dreson";
const attributeNameCurrentContextDreson = "current-context-dreson";
const shouldSendTracking = "send-tracking";
const attributeNameTrackingParentId = "tracking-parent-id";
const attributeNameSendEvents = "send-events";
const attributeNameRemoveExpired = "remove-expired";
const shouldAddFadeInAnimation = "add-fade-in-animation";
const loadOnVisibility = "load-on-visibility";

const trackingState = "tracking-state";

const log = createLogger("tag", "tag");
const BENEFIT_TAG_NAME = "ft9-benefit-tag-v1";

/*                                                                                                   */
/*                                                                              */
const MAX_CLEANUP_PERIOD_IN_MILLIS = 20 * 24 * 60 * 60 * 1000;
const MIN_RATIO_TO_FADE_IN = 0.8;

export class BenefitTagElement extends HTMLElement {
	static observedAttributes = [
		attributeNameId,
		attributeNameBenefitId,
		attributeNameOriginFeature,
		attributeNameTeam,
		attributeNameVariationId,
		attributeNameTargetContextDreson,
		attributeNameCurrentContextDreson,
		shouldSendTracking,
		attributeNameTrackingParentId,
		attributeNameSendEvents,
		attributeNameRemoveExpired,
		shouldAddFadeInAnimation,
		loadOnVisibility,
	];
	private _isConnected = false;

	override get isConnected() {
		return this._isConnected;
	}

	override set isConnected(value: boolean) {
		this._isConnected = value;
	}

	constructor() {
		super(); /*                                     */
		this.isConnected = false;
	}

	async attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		this.firstElementChild?.remove();
		if (this.isConnected && oldValue !== newValue) {
			log.info("Attribute update: ", name, oldValue, newValue, this.isConnected);
			await this.callBackend();
		}
	}

	async connectedCallback() {
		this.isConnected = true;
		await this.callBackend();
	}

	async callBackend() {
		if (this.hasAttribute(loadOnVisibility)) {
			registerVisibilityObserver(this, () => {
				/*                                                                                                  */
				this.removeAttribute(loadOnVisibility);
			});
			return;
		}

		const id = this.getAttribute(attributeNameId);
		const urlSearchParams = this.getSearchParams();

		log.info("Calling tag for %s with %s", id, JSON.stringify(urlSearchParams));

		try {
			const response = await fetchRetry({
				url: `/benefit-tag/tag?${urlSearchParams}`,
				namespace: "tag",
				fetchOptions: {
					headers: {
						Accept: "text/html;charset=UTF-8",
						"X-Requested-With": "XMLHttpRequest",
					},
					priority: "auto",
				},
				retryOptions: {
					limit: 2,
					delay: 50,
				},
			});
			if (!response.ok) {
				log.error("tag endpoint returns a non ok response!", response.status);

				captureError(
					new Error("callBackend error"),
					{
						team: "ft9",
						service: "tag",
						module: "tag",
					},
					{
						type: "ft9-benefit-tag-not-ok",
						custom: {
							url_search_params: urlSearchParams.toString(),
							status_codes: response.status.toString(),
							tries: 1,
						},
					},
				);
				return;
			}
			response.text().then((text) => {
				this.innerHTML = text;
				initTag(this);
				preventClickBubbling(this);
			});
		} catch (error) {
			log.error("tag request returns an error!", error);

			captureError(
				new Error("callBackend error"),
				{
					team: "ft9",
					service: "tag",
					module: "tag",
				},
				{
					type: "ft9-benefit-tag-not-ok",
					custom: {
						url_search_params: urlSearchParams.toString(),
						status_codes: (error as any).errors ?? "",
						tries: 3,
					},
				},
			);
		}
	}

	private getSearchParams() {
		const urlSearchParams = new URLSearchParams({});

		function addToSearchParam(paramName: string, value: string | null) {
			if (value != null) {
				urlSearchParams.append(paramName, value);
			}
		}

		addToSearchParam("benefitId", this.getAttribute(attributeNameBenefitId));
		addToSearchParam("originFeature", this.getAttribute(attributeNameOriginFeature));
		addToSearchParam("team", this.getAttribute(attributeNameTeam));
		addToSearchParam("variationId", this.getAttribute(attributeNameVariationId));
		addToSearchParam("targetContextPsr", this.getAttribute(attributeNameTargetContextDreson));
		addToSearchParam("currentContextPsr", this.getAttribute(attributeNameCurrentContextDreson));
		return urlSearchParams;
	}
}

function initTag(initializingWrapper: BenefitTagElement) {
	const tag = initializingWrapper.firstElementChild;
	if (!(tag && tag instanceof HTMLElement)) {
		return;
	}

	/*                                              */
	if (initializingWrapper.hasAttribute(attributeNameRemoveExpired) && tag.dataset.expirationDate) {
		const expirationDate = new Date(tag.dataset.expirationDate);

		const delayInMillis: number = Math.min(Math.max(expirationDate.getTime() - new Date().getTime(), 1), MAX_CLEANUP_PERIOD_IN_MILLIS);
		window.setTimeout(() => {
			tag.remove();
		}, delayInMillis);
	}

	/*              */
	const trackingParentId = initializingWrapper.getAttribute(attributeNameTrackingParentId);
	sendTracking(initializingWrapper, tag, trackingParentId);

	registerVisibilityObserver(initializingWrapper, () => {
		if (initializingWrapper.hasAttribute(shouldAddFadeInAnimation)) {
			initializingWrapper.classList.add("benefit-tag__visible");
		}
	});
}

function sendTracking(initializingWrapper: BenefitTagElement, tag: HTMLElement, trackingParentId: string | null) {
	if (!initializingWrapper.hasAttribute(shouldSendTracking)) {
		return;
	}

	const trackingData = tag.dataset.trackingLabels;
	if (!trackingData) {
		return;
	}

	let feature = {
		id: generateRandomString(20),
		name: "Benefit",
		status: "loaded" as FeatureStatus,
		labels: JSON.parse(trackingData) as DataContainer,
	} as Feature;

	if (trackingParentId) {
		feature = {
			...feature,
			parentId: trackingParentId,
		};
	}

	submitMerge({}, [feature]);
}

function registerVisibilityObserver(initializingWrapper: BenefitTagElement, callback: () => void) {
	const options: IntersectionObserverInit = {
		threshold: MIN_RATIO_TO_FADE_IN,
	};

	const observer = new IntersectionObserver((entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
		for (const entry of entries) {
			if (entry.intersectionRatio >= MIN_RATIO_TO_FADE_IN) {
				callback();
				observer.unobserve(entry.target);
			}
		}
	}, options);
	observer.observe(initializingWrapper);
}

if (customElements && customElements.get(BENEFIT_TAG_NAME) === undefined && !toggle.get("ftnine_emergency_custom_element_disable_tag", false)) {
	customElements.define(BENEFIT_TAG_NAME, BenefitTagElement);
}
